/**
 * Copyright (c) Omnissa, LLC. All rights reserved.
 * This product is protected by copyright and intellectual property laws in the
 * United States and other countries as well as by international treaties.
 * -- Omnissa Restricted
 *
 * @format
 */

import Logger from "../../../core/libs/logger";
import { ClientSettingModel } from "../model/client-setting-model";
import { Injectable } from "@angular/core";
import { timezoneMap } from "./timezone-history.consts";
import moment from "moment-timezone";

@Injectable({
   providedIn: "root"
})
export class Timezone {
   public timezoneNotInMap: boolean = false;
   public timeZoneMap = timezoneMap;
   private adminSettings;
   private logger: Logger;

   constructor(private clientSettingModel: ClientSettingModel) {
      this.logger = new Logger(Logger.TIMEZONE);
   }
   /*
    * https://omnissa.atlassian.net/wiki/spaces/EWA/pages/105453322/VDM+-+ClientBrokerXml
    * TimeOffset_GMT : -07:00
    * TZID : Europe/London
    * Windows_Timezone: GMT Standard Time
    */

   private getTimezoneOffsetData = (): string => {
      let offset: number, r: string, h: string;
      //@ts-ignore
      offset = new window.Date().getTimezoneOffset();

      r = offset < 0 ? "+" : "-";
      offset = offset < 0 ? 0 - offset : offset;

      const temp: Array<string> = String(offset / 60).split(".");
      h = temp[0].length > 1 ? temp[0] : "0" + temp[0];
      r += h;
      r += ":";

      r += temp.length > 1 ? "30" : "00";

      this.logger.info("Timezone offset: " + r);
      return r;
   };

   /*
    *---------------------------------------------------------------------------
    *
    * getWindowsTimezone  --
    *
    * This function will return the timezone region, it is used in
    *  1. get timezone on opening setting dialog
    *  2. get timezone on launching desktop
    * If it needs load from Google policy, directly return the region in policy
    * If need to load locally, get from server returned value.
    *
    * Results:
    *      string: Timezone region
    *
    *---------------------------------------------------------------------------
    */

   public getWindowsTimezone = (): string => {
      if (this.loadAdminPolicy()) {
         const region = this._getTimezoneFromPolicy();
         if (region) {
            this.logger.info("Timezone got from  policy: " + region);
            return region;
         } else {
            this.logger.info("Timezone in policy isn't valid, please refer to user guide.");
         }
      }
      if (this.adminSettings?.timezoneSync && this._useSystemTimezone()) {
         this._saveSystemTimezone();
      }

      const timezone = this.clientSettingModel.getStringItem("timezoneSync");
      if (timezone === "" || timezone === undefined) {
         let windowsTimezone = this.windowsTimezoneHandler();
         this.logger.info("Auto get Timezone data: " + windowsTimezone);
         return windowsTimezone;
      } else {
         this.logger.info("Prefer timezone user preference: " + timezone);
         return timezone.toString();
      }
   };

   /*
    *---------------------------------------------------------------------------
    *
    * _useSystemTimezone  --
    *
    * Check if it needs to load system timezone when timezone policy exists
    *
    * Results:
    *     boolean
    *
    *---------------------------------------------------------------------------
    */

   private _useSystemTimezone = () => {
      let result: boolean = false;
      const isChanged = this.clientSettingModel.getBooleanItem("isTimezoneChanged");
      if (this.adminSettings?.timezoneSync?.isSync) {
         if (!this.adminSettings.editable?.timezoneSync || (this.adminSettings.editable?.timezoneSync && !isChanged)) {
            this.logger.debug("Timezone auto sync is enabled in policy, use system timezone");
            result = true;
         }
      }
      if (!this.adminSettings?.timezoneSync?.isSync && !this.adminSettings?.timezoneSync?.timezone) {
         if (!this.adminSettings.editable?.timezoneSync || (this.adminSettings.editable?.timezoneSync && !isChanged)) {
            this.logger.debug(
               "[Timezone customized sync is enabled but no customized value in policy, use system timezone"
            );
            result = true;
         }
      }
      return result;
   };

   private _saveSystemTimezone = () => {
      //save system's timezone in server
      let windowsTimezone = this.windowsTimezoneHandler();
      this.clientSettingModel.updateSetting("timezoneSync", windowsTimezone);
   };

   /*
    *---------------------------------------------------------------------------
    *
    * loadAdminPolicy  --
    *
    * Only load policy's timezone when
    * 1. timezone is editable and timezone offset exists and hasn't been changed OR
    * 2. timezone is not editable and timezone offset exists
    *
    * Results:
    *     boolean
    *
    *---------------------------------------------------------------------------
    */

   public loadAdminPolicy = () => {
      this.adminSettings = this.clientSettingModel.getAdminSettings();
      if (!this.adminSettings) {
         this.logger.debug("[Google policy] No policy, load timezone locally.");
         return false;
      }
      if (!this.adminSettings.timezoneSync) {
         this.logger.debug("[Google policy] No timezone policy, load timezone locally");
         return false;
      } else {
         if (!this.adminSettings.timezoneSync.isSync) {
            if (!this.adminSettings.timezoneSync.timezone) {
               this.logger.debug("[Google policy] No timezone value, load timezone locally");
               return false;
            } else {
               this.logger.debug("[Google policy] There is timezone value.");
               const editable = this.adminSettings.editable?.timezoneSync;
               if (editable) {
                  this.logger.debug("[Google policy] Timezone is editable");
                  const isChanged = this.clientSettingModel.getBooleanItem("isTimezoneChanged");
                  if (isChanged) {
                     this.logger.debug("[Google policy] Timezone has changed, load locally");
                  } else {
                     this.logger.debug("[Google policy] Timezone doesn't changed, load policy");
                  }
                  return !isChanged;
               } else {
                  this.logger.debug("[Google policy] Timezone is not editable");
                  return true;
               }
            }
         } else {
            this.logger.debug("[Google policy] Timezone sync is disabled, don't need to get policy");
            return false;
         }
      }
   };

   /*
    *---------------------------------------------------------------------------
    *
    * isAdminPolicyTakingAffect  --
    *
    * Kill-switch toggler needs to be reset by code when policy matches:
    * 1. timezone policy exists and not editable OR
    * 2. timezone is editable and hasn't been changed
    *
    * Results:
    *     boolean
    *
    *---------------------------------------------------------------------------
    */

   public isAdminPolicyTakingAffect = () => {
      if (this.adminSettings?.timezoneSync) {
         if (!this.adminSettings.editable?.timezoneSync) {
            return true;
         } else {
            const isChanged = this.clientSettingModel.getBooleanItem("isTimezoneChanged");
            return !isChanged;
         }
      }
      return false;
   };

   private _getTimezoneFromPolicy = () => {
      const timeZone = this.adminSettings?.timezoneSync?.timezone;
      return this.timeZoneMap[timeZone];
   };

   private windowsTimezoneHandler = (): string => {
      return moment.tz.guess();
   };

   public getTimeZoneInfo = () => {
      return {
         location: Intl.DateTimeFormat().resolvedOptions().timeZone,
         timezoneOffset: this.getTimezoneOffsetData()
      };
   };

   /**
    * getActualOffset --
    * isDST --
    * isDSTDisabledByOS --
    *
    * Determines whether Daylight Saving Time (DST) is currently disabled by the OS
    * for a given IANA time zone.
    *
    * The function works by comparing the theoretical DST status of the current date
    * in the specified time zone against the actual UTC offset provided by the OS.
    * If the time zone uses DST and the OS offset does not reflect the DST adjustment,
    * the function returns true, indicating that DST is effectively disabled by the OS.
    *
    * ┌──────────────┬───────────────────────────────┬──────────────┬────────────────┬──────────────────┬──────────┐
    * │   Location   │ Local Time                    │ actualOffset │ standardOffset │ theoreticallyDST │ Return   │
    * ├──────────────┼───────────────────────────────┼──────────────┼────────────────┼──────────────────┼──────────┤
    * │ India        │ Sep 7 2025 17:05 GMT+0530     │ -330         │ -330           │ false            │ false    │
    * │ London       │ Sep 7 2025 12:12 GMT+0100 BST │ -60          │ 0              │ true             │ false    │
    * │ London       │ Sep 7 2025 12:12 GMT+0100 BST │  0           │ 0              │ true             │ true     │
    * │ Beijing      │ Sep 7 2025 19:14 GMT+0800 CST │ -480         │ -480           │ false            │ false    │
    * │ Los Angeles  │ Sep 7 2025 04:15 GMT-0700 PDT │ 420          │ 480            │ true             │ false    │
    * │ Los Angeles  │ Sep 7 2025 04:15 GMT-0700 PDT │ 480          │ 480 (480/420)  │ true             │ true     │
    * │ Santiago     │ Sep 7 2025 04:15 GMT-0300     │ 180          │ 240 (180/240)  │ true             │ false    │
    * │ Sydney       │ Sep 7 2025 04:15 GMT+1000     │ -600         │-600 (-660/-600)│ false            │ false    │
    * │--------------│-------------------------------│--------------│----------------│------------------│----------│
    * │ London (Feb) │ Feb 1 2025 12:00 GMT+0000 GMT │ 0            │ 0              │ false            │ false    │
    * │ LA (Feb)     │ Feb 1 2025 04:00 GMT-0800 PST │ 480          │ 480            │ false            │ false    │
    * │ Sydney       │ Feb 1 2025 04:15 GMT+1000     │ -600         │-600 (-660/-600)│ true             │ true     │
    * │ Sydney       │ Feb 1 2025 04:15 GMT+1000     │ -660         │-600 (-660/-600)│ true             │ false    │
    * └──────────────┴───────────────────────────────┴──────────────┴────────────────┴──────────────────┴──────────┘
    *
    *  Notes:
    * - `actualOffset` = current offset returned by moment (converted to JS convention).
    * - `standardOffset` = offset during standard (non-DST) time, derived from Jan/Jul.
    * - `theoreticallyDST` = whether the time zone *should* be in DST at the given date.
    *
    * Notes:
    * - Santiago -33.4489 / -70.6693 America/Santiago
    * - Sydney.  -33.8688 / 151.2093 Australia/Sydney
    *
    * @param timeZone - Optional IANA time zone string (e.g., "America/New_York").
    *                   If not provided, the system's current time zone is used.
    * @returns True if DST appears to be disabled by the OS, false otherwise.
    */

   private getActualOffset(): number {
      const now = new Date();
      return now.getTimezoneOffset();
   }

   private isDST(now: moment.Moment): boolean {
      return now.isDST();
   }

   public isDSTDisabledByOS = (timeZone?: string): boolean => {
      if (timeZone && /^[+-]\d{2}:\d{2}$/.test(timeZone)) {
         this.logger.info(
            `Input timeZone format "${timeZone}" is a fixed UTC offset, not an IANA time zone. Returning false.`
         );
         return false;
      }

      const tz = timeZone || moment.tz.guess();
      const now = moment.tz(tz);
      const zone = moment.tz.zone(tz);
      if (!zone) {
         this.logger.info(`Invalid IANA time zone: ${tz}`);
         return false;
      }

      const year = now.year();
      const janOffset = zone.utcOffset(Date.UTC(year, 0, 1));
      const julOffset = zone.utcOffset(Date.UTC(year, 6, 1));

      const hasDST = janOffset !== julOffset;
      const theoreticallyDST = hasDST && this.isDST(now);

      /**
       * Offset conventions:
       *
       * Moment.js `utcOffset()`:
       *   - Eastern Hemisphere (UTC+X) → negative value (e.g., Beijing = -480)
       *   - Western Hemisphere (UTC−X) → positive value (e.g., New York during DST = +240)
       *
       * JavaScript `Date.getTimezoneOffset()`:
       *   - Eastern Hemisphere (UTC+X) → positive value
       *   - Western Hemisphere (UTC−X) → negative value
       */
      const actualOffset = this.getActualOffset();
      let standardOffset = Math.max(janOffset, julOffset);

      return theoreticallyDST && actualOffset === standardOffset;
   };
}
